import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Info,
  PlayArrow
} from '@mui/icons-material';

interface ValidationResult {
  level: 'error' | 'warning' | 'info' | 'success';
  message: string;
  line?: number;
}

const NetworkValidation: React.FC = () => {
  const [configInput, setConfigInput] = useState('');
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  const handleValidate = async () => {
    setIsValidating(true);
    // Simulate validation
    setTimeout(() => {
      setValidationResults([
        { level: 'success', message: 'Configuration syntax is valid' },
        { level: 'warning', message: 'Missing NTP server configuration', line: 15 },
        { level: 'info', message: 'Consider enabling port security on access ports' },
        { level: 'error', message: 'Invalid VLAN ID: 5000 (must be 1-4094)', line: 45 }
      ]);
      setIsValidating(false);
    }, 1500);
  };

  const getIcon = (level: string) => {
    switch (level) {
      case 'error': return <Error color="error" />;
      case 'warning': return <Warning color="warning" />;
      case 'info': return <Info color="info" />;
      case 'success': return <CheckCircle color="success" />;
      default: return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Network Configuration Validation
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Validate your network configurations against best practices and standards
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuration Input
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={20}
              placeholder="Paste your configuration here..."
              value={configInput}
              onChange={(e) => setConfigInput(e.target.value)}
              sx={{
                fontFamily: 'monospace',
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem'
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={handleValidate}
              disabled={!configInput || isValidating}
              sx={{ mt: 2 }}
            >
              {isValidating ? 'Validating...' : 'Validate Configuration'}
            </Button>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Validation Results
              </Typography>
              {validationResults.length > 0 ? (
                <List>
                  {validationResults.map((result, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {getIcon(result.level)}
                      </ListItemIcon>
                      <ListItemText
                        primary={result.message}
                        secondary={result.line ? `Line ${result.line}` : undefined}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No validation results yet. Paste a configuration and click Validate.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Validation Rules
              </Typography>
              <Stack spacing={1}>
                <Chip label="VLAN Range Check" size="small" />
                <Chip label="IP Address Validation" size="small" />
                <Chip label="Security Best Practices" size="small" />
                <Chip label="Redundancy Check" size="small" />
                <Chip label="MTU Consistency" size="small" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NetworkValidation;